// eslint-disable-next-line import/no-cycle
import {
  WidgetTypeConfig,
  ConfigurationPanelStructureItemEnum,
  ComponentNameEnum,
  Property,
  TemplateStructureEnum,
  ElementPartTypeEnum,
  StepperSignEnum,
} from '@/types';
// eslint-disable-next-line import/no-cycle
import {
  saveFormFieldConfig,
  setContent,
  setCssProperty,
  setCssPropertyAndRemoveIfZero,
  toggleClass,
} from '@/components/template-builder/setters';
// eslint-disable-next-line import/no-cycle
import {
  getContentWithLineBreaks,
  getCssPropertyByName,
  hasClass,
} from '@/components/template-builder/getters';
// eslint-disable-next-line import/no-cycle
import {
  getConsentChannel, getTrackingConsent, getConsentMode, getConsentRequired,
} from '@/components/template-builder/getters/form';
// eslint-disable-next-line import/no-cycle
import {
  setConsentChannel, setTrackingConsentGrouped, setConsentMode, setConsentRequired,
} from '@/components/template-builder/setters/form';
// eslint-disable-next-line import/no-cycle
import {
  hasEmailWidgetInFunnel,
  hasPhoneWidgetInFunnel,
  getUsedConsentChannelsInTemplate,
} from '@/composables/template-editor/TemplateEditor';
import variables from '@/components/template-builder/utils/variables-list';
// eslint-disable-next-line import/no-cycle
import getInputVariablesFieldConfig from '@/components/template-builder/config/fields/input-variables-config';
// eslint-disable-next-line import/no-cycle
import getDisplayFieldConfig from '@/components/template-builder/config/fields/display-config';
// eslint-disable-next-line import/no-cycle
import getColorFieldConfig from '@/components/template-builder/config/fields/color-config';
// eslint-disable-next-line import/no-cycle
import bordersFieldConfig from '@/components/template-builder/config/fields/borders-config';
// eslint-disable-next-line import/no-cycle
import getWidgetCommonStylesGroup from '@/components/template-builder/config/fields/widget-common-styles-config';
// eslint-disable-next-line import/no-cycle
import getStepperFieldConfig from '@/components/template-builder/config/fields/stepper-config';
// eslint-disable-next-line import/no-cycle
import fontFieldConfig from '@/components/template-builder/config/fields/font-config';
// eslint-disable-next-line import/no-cycle
import getVerticalSpacesFieldConfig from '@/components/template-builder/config/fields/vertical-spaces-config';
// eslint-disable-next-line import/no-cycle
import getHorizontalSpacesFieldConfig from '@/components/template-builder/config/fields/horizontal-spaces-config';
// eslint-disable-next-line import/no-cycle
import { i18n } from '@/i18n';

const widgetConsentCheckboxConfigV4: WidgetTypeConfig = {
  tabPanel: {
    label: 'templateBuilder.widgetsHeaders.consentCheckbox',
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
        label: 'templateBuilder.configs.widgetConfiguration',
        selector: 'body #spm_body #{ID}',
        items: [
          {
            label: 'templateBuilder.fields.consentChannel',
            selector: '{SELECTOR}',
            properties: [{
              name: 'data-consent-channel',
              getter: getConsentChannel,
              setters: [setConsentChannel],
            }],
            componentName: ComponentNameEnum.DROPDOWN,
            options: {
              defaultValue: 'email',
              list: [
                { value: 'email', label: 'templateBuilder.fields.consentChannelOptions.email' },
                { value: 'sms', label: 'templateBuilder.fields.consentChannelOptions.sms' },
                { value: 'whatsapp', label: 'templateBuilder.fields.consentChannelOptions.whatsapp' },
              ],
              isOptionDisabled: (value: string): boolean => {
                // Check if the identifier widget is present
                if (value === 'email' && !hasEmailWidgetInFunnel()) return true;
                if ((value === 'sms' || value === 'whatsapp') && !hasPhoneWidgetInFunnel()) return true;
                // Check if this channel is already used by a consent checkbox
                // (Dropdown.vue protects the currently selected value from being disabled)
                const usedChannels = getUsedConsentChannelsInTemplate();
                return usedChannels.includes(value);
              },
            },
          },
          {
            label: 'templateBuilder.formFields.label',
            selector: '{SELECTOR} .spm_widget_input_checkbox__options label',
            properties: [{ name: 'content', getter: getContentWithLineBreaks, setters: [setContent] }],
            fieldId: 'label',
            ...getInputVariablesFieldConfig((i18n.global as any).t('templateBuilder.fields.consentDefaultLabels.email'), variables.allVariables, false, true),
          },
          {
            // Facultative par défaut : le formulaire part même case décochée (consentement refusé).
            label: 'templateBuilder.fields.consentRequired',
            selector: '{SELECTOR}',
            properties: [{
              name: 'data-consent-required',
              getter: getConsentRequired,
              setters: [setConsentRequired],
            }],
            componentName: ComponentNameEnum.CHECKBOX,
            options: {
              defaultValue: false,
            },
          },
          {
            // CNIL pixels — mode groupé : la case (email) couvre aussi le suivi des
            // ouvertures. Affiché si canal = email ET pas en mode granulaire.
            label: 'templateBuilder.fields.trackingConsentGrouped',
            selector: '{SELECTOR}',
            displayHandler: (selector: string) => getConsentChannel(selector) === 'email' && getConsentMode(selector) !== 'true',
            properties: [{
              name: 'data-tracking-consent',
              getter: getTrackingConsent,
              setters: [setTrackingConsentGrouped],
            }],
            componentName: ComponentNameEnum.CHECKBOX,
            options: {
              defaultValue: true,
            },
          },
          {
            // CNIL pixels — mode GRANULAIRE (« Gérer finement ») : case maitre + sous-toggles
            // par finalité. Email-only ET seulement si le suivi groupé est inclus (sans suivi
            // à séparer, le granulaire n'a aucun intérêt). Défaut = groupé.
            label: 'templateBuilder.fields.consentMode',
            selector: '{SELECTOR}',
            displayHandler: (selector: string) => getConsentChannel(selector) === 'email' && getTrackingConsent(selector) === 'true',
            properties: [{
              name: 'data-consent-mode',
              getter: getConsentMode,
              setters: [setConsentMode],
            }],
            componentName: ComponentNameEnum.CHECKBOX,
            options: {
              defaultValue: false,
            },
          },
        ],
      },
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        label: 'templateBuilder.configs.widgetStyles',
        tabItemType: TemplateStructureEnum.GROUP,
        canSave: true,
        elementPartType: ElementPartTypeEnum.STYLES,
        tabPanel: {
          items: [
            {
              type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
              label: 'templateBuilder.formFields.appearanceStyles',
              selector: 'body #spm_body #{ID}',
              items: [
                {
                  label: 'templateBuilder.fields.checkboxSize',
                  selector:
                    '{SELECTOR} .spm_form_field input.spm_widget_input_radio__input',
                  properties: [
                    { name: 'width', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'height', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'min-width', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'min-height', getter: getCssPropertyByName, setters: [setCssProperty] },
                  ],
                  ...getStepperFieldConfig(StepperSignEnum.PX, 1, 12, 40),
                },
                {
                  label: 'templateBuilder.fields.borders',
                  selector:
                    '{SELECTOR} .spm_form_field input.spm_widget_input_radio__input',
                  properties: [
                    { name: 'border-color', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'border-style', getter: getCssPropertyByName, setters: [] },
                    { name: 'border-width', getter: getCssPropertyByName, setters: [setCssPropertyAndRemoveIfZero] },
                  ],
                  ...bordersFieldConfig,
                },
                {
                  label: 'templateBuilder.fields.checkboxCheckedBackground',
                  selector:
                    '{SELECTOR} .spm_form_field input.spm_widget_input_radio__input:checked',
                  properties: [
                    { name: 'background', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'border-color', getter: getCssPropertyByName, setters: [setCssProperty] },
                  ],
                  ...getColorFieldConfig(undefined, false, true),
                },
                {
                  label: 'templateBuilder.fields.checkboxCheckmarkColor',
                  selector:
                    '{SELECTOR} .spm_form_field input.spm_widget_input_radio__input:checked::after',
                  properties: [
                    { name: 'border-color', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'background', getter: getCssPropertyByName, setters: [setCssProperty] },
                  ],
                  ...getColorFieldConfig(undefined, false, true),
                },
                {
                  label: 'templateBuilder.fields.checkboxLabelFont',
                  selector:
                    '{SELECTOR} .spm_widget_input_checkbox__options label',
                  properties: [
                    { name: 'font-family', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'font-size', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'font-weight', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'font-style', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'text-decoration-line', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'text-transform', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'color', getter: getCssPropertyByName, setters: [setCssProperty] },
                  ],
                  ...fontFieldConfig,
                },
                {
                  label: 'templateBuilder.fields.checkboxItemMargin',
                  selector:
                    '{SELECTOR} .spm_widget_input_checkbox__options',
                  properties: [
                    { name: 'margin-top', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'margin-bottom', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'margin-left', getter: getCssPropertyByName, setters: [setCssProperty] },
                    { name: 'margin-right', getter: getCssPropertyByName, setters: [setCssProperty] },
                  ],
                  componentName: ComponentNameEnum.SPACES,
                  options: {
                    ...getVerticalSpacesFieldConfig('margin').options,
                    ...getHorizontalSpacesFieldConfig('margin').options,
                  },
                },
              ],
            },
            getWidgetCommonStylesGroup(),
          ],
        },
      },
    ],
  },
};

export default widgetConsentCheckboxConfigV4;
